import { useEffect, useState, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet-geometryutil";
import markerIconPng from "../img/Coords_icon.svg";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";
import Coords from "../img/Coords_icon.svg";
import Distance from "../img/Distance_icon.svg";
import Area from "../img/Area_icon.svg";

const MeasureControl = (props) => {
  const map = props.map;
  const [mode, setMode] = useState(null);
  const [points, setPoints] = useState([]);
  const [layer, setLayer] = useState(null);
  const [layerGroup, setLayerGroup] = useState(null);
  const [pointMarkers, setPointMarkers] = useState([]);
  const [coordsValue, setCoordsValue] = useState(null);
  const [distanceValue, setDistanceValue] = useState(null);
  const [areaValue, setAreaValue] = useState(null);
  const guideLineRef = useRef(null);
  const lastMouseEventRef = useRef(null);

  const defaultIcon = L.icon({
    iconUrl: markerIconPng,
    shadowUrl: markerShadowPng,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  useEffect(() => {
    const mapContainer = map?.getContainer();
    if (!mapContainer || document.getElementById("cursor-tooltip")) return;

    const tooltip = document.createElement("div");
    tooltip.id = "cursor-tooltip";
    tooltip.style.display = "none";

    mapContainer.appendChild(tooltip);

    return () => {
      if (tooltip && mapContainer.contains(tooltip)) {
        mapContainer.removeChild(tooltip);
      }
    };
  }, [map]);

  const changeCursor = useCallback((isMeasuring) => {
    if (!map) return;
    if (isMeasuring) {
      map.getContainer().style.cursor = "crosshair";
    } else {
      map.getContainer().style.cursor = "";
    }
  }, [map]);

  const stopMeasurement = useCallback(() => {
    setMode(null);
    changeCursor(false);

    if (layer) {
      map.removeLayer(layer);
      setLayer(null);
    }

    if (guideLineRef.current) {
      map.removeLayer(guideLineRef.current);
      guideLineRef.current = null;
    }

    pointMarkers.forEach((m) => map.removeLayer(m));
    setPointMarkers([]);

    setPoints([]);
    setCoordsValue(null);
    setDistanceValue(null);
    setAreaValue(null);

    const tooltipEl = document.getElementById("cursor-tooltip");
    if (tooltipEl) {
      tooltipEl.style.display = "none";
      tooltipEl.style.left = `0px`;
      tooltipEl.style.top = `0px`;
    }
  }, [map, layer, changeCursor, pointMarkers]);

  useEffect(() => {
    if (props.activeWidget !== "Measurement") {
      stopMeasurement();
      changeCursor(false)
    }
  }, [props.activeWidget, stopMeasurement, changeCursor]);

  useEffect(() => {
    if (props.activeWidget === "Measurement") {
      const panel = document.querySelector(".widget-panel-right");
      const buttons = document.querySelector(".buttons-container-right");
      if (panel && props.isMobile) {
        if (mode) {
          panel.style.display = "none";
          buttons.style.display = "";
        } else {
          panel.style.display = "";
          buttons.style.display = "none";
        }
      }
    }
  }, [mode, props.isMobile, props.activeWidget]);

  useEffect(() => {
    if (!map) return;

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    setLayerGroup(drawnItems);

    const draw = new L.Control.Draw({
      draw: false,
      edit: {
        featureGroup: drawnItems,
        edit: false,
        remove: false,
      },
    });
    map.addControl(draw);

    if (!map.getPane("pointsPane")) {
      map.createPane("pointsPane");
      map.getPane("pointsPane").style.zIndex = 650;
    }

    if (!map.getPane("linesPane")) {
      map.createPane("linesPane");
      map.getPane("linesPane").style.zIndex = 600;
    }

    drawnItems.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        layer.setPane("pointsPane");
      }
      if (layer instanceof L.Polyline || layer instanceof L.Polygon) {
        layer.setPane("linesPane");
      }
    });

    return () => {
      map.removeControl(draw);
      map.removeLayer(drawnItems);
      setLayerGroup(null);
    };
  }, [map]);

  const renderTooltipContent = useCallback(() => {
    let content = "";
    if (mode === "coords") {
      content = "Click to get coordinates";
    }
    else if (mode === "distance") {
      if (points.length > 1){
        content = "Click last point to end measuring";
      }
      else if (points.length > 0) {
        content = "Add at least two points";
      }
      else {
        content = "Click to start measuring";
      }
    }
    else if (mode === "area") {
      if (points.length > 2) {
        content = "Click first point to end measuring";
      } else if (points.length > 0) {
        content = "Add at least three points";
      }
      else if (points.length === 0) {
        content = "Click to start measuring";
      }
    }
    return content;
  }, [mode, points.length])

  const updateTooltipPosition = useCallback((e) => {
    const tooltipEl = document.getElementById("cursor-tooltip");
    if (!tooltipEl || !map) return;

    let containerPoint;

    if (e.latlng) {
      containerPoint = map.latLngToContainerPoint(e.latlng);
    } else if (e.originalEvent && e.originalEvent.clientX != null) {
      const { clientX, clientY } = e.originalEvent;
      const mapContainer = map.getContainer().getBoundingClientRect();
      containerPoint = {
        x: clientX - mapContainer.left,
        y: clientY - mapContainer.top,
      };
    } else {
      return;
    }

    tooltipEl.style.display = "block";
    tooltipEl.style.left = `${containerPoint.x + 15}px`;
    tooltipEl.style.top = `${containerPoint.y + 15}px`;
    tooltipEl.innerText = renderTooltipContent();
  }, [map, renderTooltipContent]);

  const clearLayer = useCallback(() => {
    if (!map) return;
    if (layer) {
      map.removeLayer(layer);
      setLayer(null);
    }
    pointMarkers.forEach((m) => map.removeLayer(m));
    setPointMarkers([]);
    setPoints([]);
    if (guideLineRef.current) {
      map.removeLayer(guideLineRef.current);
      guideLineRef.current = null;
    }
  }, [layer, map, pointMarkers]);

  const finishMeasurement = useCallback((finalPoints) => {
    if (mode === "distance" && finalPoints.length >= 2) {
      let totalDistance = 0;
      for (let i = 1; i < finalPoints.length; i++) {
        totalDistance += finalPoints[i - 1].distanceTo(finalPoints[i]);
      }
      setDistanceValue((totalDistance / 1000).toFixed(2));
    }

    if (mode === "area" && finalPoints.length >= 3) {
      const area = L.GeometryUtil.geodesicArea(finalPoints);
      const formattedArea = (area / 1000000).toFixed(2);
      setAreaValue(formattedArea);
    }

    pointMarkers.forEach((m) => map.removeLayer(m));
    setPointMarkers([]);
    setPoints([]);
    setMode(null);
    changeCursor(false);

    if (guideLineRef.current) {
      map.removeLayer(guideLineRef.current);
      guideLineRef.current = null;
    }
    const tooltipEl = document.getElementById("cursor-tooltip");
    if (tooltipEl) tooltipEl.style.display = "none";
  }, [changeCursor, map, mode, pointMarkers]);

  useEffect(() => {
    if (!map || !["distance", "area", "coords"].includes(mode)) return;

    const tooltipEl = document.getElementById("cursor-tooltip");
    if (tooltipEl) {
      tooltipEl.style.display = "block";
      tooltipEl.innerText = renderTooltipContent();
    }

    const onMouseMove = (e) => {
      lastMouseEventRef.current = e;
      updateTooltipPosition(e);

      if (points.length === 0) return;

      const lastPoint = points[points.length - 1];
      const current = e.latlng;

      if (guideLineRef.current) {
        map.removeLayer(guideLineRef.current);
        guideLineRef.current = null;
      }

      const tempLine = L.polyline([lastPoint, current], {
        color: "#615C9F",
        weight: 2,
        dashArray: "5, 10",
        opacity: 0.75,
        interactive: false,
        pane: "linesPane",
      }).addTo(layerGroup);

      guideLineRef.current = tempLine;
    };

    const onClickOrDown = (e) => {
      updateTooltipPosition(e);
    };

    map.on("mousemove", onMouseMove);
    map.on("mousedown", onClickOrDown);
    map.on("click", onClickOrDown);

    return () => {
      map.off("mousemove", onMouseMove);
      map.off("mousedown", onClickOrDown);
      map.off("click", onClickOrDown);
      if (tooltipEl) tooltipEl.style.display = "none";
      if (guideLineRef.current) {
        map.removeLayer(guideLineRef.current);
        guideLineRef.current = null;
      }
    };
  }, [map, mode, points, layerGroup, renderTooltipContent, updateTooltipPosition]);

  useEffect(() => {
    if (!map || !mode || props.activeWidget !== "Measurement") return;

    const handleClick = (e) => {
      const { lat, lng } = e.latlng;

      const tooltipEl = document.getElementById("cursor-tooltip");
      if (tooltipEl && (mode === "distance" || mode === "area")) {
        updateTooltipPosition(e);
      }

      if (mode === "coords") {
        setCoordsValue({ lat, lng });
        clearLayer();
        const marker = L.marker({ lat, lng }, { icon: defaultIcon }).addTo(layerGroup);
        setLayer(marker);
        setMode(null);
        changeCursor(false);
        return;
      }

      const clickedPoint = L.latLng(lat, lng);
      const newPoints = [...points, clickedPoint];

      if (mode === "area" && points.length >= 2 && clickedPoint.equals(points[0])) {
        finishMeasurement(newPoints);
        return;
      }

      if (mode === "distance" && points.length >= 1) {
        const lastPoint = points[points.length - 1];
        if (clickedPoint.equals(lastPoint)) {
          finishMeasurement(points);
          return;
        }
      }

      setPoints(newPoints);

      const pointMarker = L.circleMarker(clickedPoint, {
        radius: 6,
        color: "#615C9F",
        opacity: 0.8,
        fillColor: "white",
        fillOpacity: 1,
        weight: 3,
        interactive: true,
        pane: "pointsPane",
      }).addTo(layerGroup);

      if (mode === "distance" && newPoints.length >= 2) {
        pointMarker.on("click", () => {
          finishMeasurement(newPoints);
        });
      }

      if (mode === "area" && points.length >= 2 && clickedPoint.equals(points[0])) {
        pointMarker.on("click", () => {
          finishMeasurement(newPoints);
        });
      }

      setPointMarkers((prev) => {
        const updatedMarkers = [...prev, pointMarker];

        if (mode === "area" && newPoints.length >= 3) {
          const firstMarker = updatedMarkers[0];

          firstMarker.setStyle({ interactive: true });
          firstMarker.on("click", () => {
            finishMeasurement(newPoints);
          });
        }

        return updatedMarkers;
      });

      if (layer) map.removeLayer(layer);

      if (mode === "distance") {
        const line = L.polyline(newPoints, {
          color: "#615C9F",
          weight: 3,
          opacity: 0.85,
          pane: "linesPane",
        }).addTo(layerGroup);
        setLayer(line);
      }
      if (mode === "area") {
        const polygon = L.polygon(newPoints, {
          color: "#615C9F",
          weight: 2,
          opacity: 0.85,
          fillOpacity: 0.3,
          pane: "linesPane",
        }).addTo(layerGroup);
        setLayer(polygon);
      }

      if (guideLineRef.current) {
        map.removeLayer(guideLineRef.current);
        guideLineRef.current = null;
      }

      if (lastMouseEventRef.current && (mode === "distance" || mode === "area")) {
        updateTooltipPosition(lastMouseEventRef.current);
      }
    };

    const handleDoubleClick = (e) => {
      if ((mode === "distance" && points.length >= 2) || (mode === "area" && points.length >= 3)) {
        finishMeasurement(points);
      }
    };

    map.on("click", handleClick);
    map.on("dblclick", handleDoubleClick);

    return () => {
      map.off("click", handleClick);
      map.off("dblclick", handleDoubleClick);
    };
  }, [map, mode, points, pointMarkers, layer, layerGroup, props.activeWidget, changeCursor, clearLayer, defaultIcon, finishMeasurement, updateTooltipPosition]);

  const setActiveMode = (type, e) => {
    e.stopPropagation();
    stopMeasurement();
    changeCursor(true);
    setMode(type);
  };

  return (
    <>
      {props.activeWidget === "Measurement" && (
        <div className="measurement-container">
          <div className="measurement-item">
            <img src={Coords} alt="Coordinates" />
            {!coordsValue ? (
              <span className="widget-text">Click on the map to get coordinates</span>
            ) : (
              <div className="measurement-display">
                <div>Latitude: {coordsValue.lat.toFixed(4)}</div>
                <div>Longitude: {coordsValue.lng.toFixed(4)}</div>
              </div>
            )}
            {mode === "coords" || coordsValue ? (
              <button className="ccl-button ccl-button--default ccl-button--default-neg" onClick={stopMeasurement} aria-label={coordsValue ? "Clear" : "Cancel"}>
                {coordsValue ? "Clear" : "Cancel"}
              </button>
            ) : (
              <button className="ccl-button ccl-button--default" onClick={(e) => setActiveMode("coords", e)} aria-label="Start">
                Start
              </button>
            )}
          </div>
          <div className="measurement-item">
            <img src={Distance} alt="Distance" />
            {!distanceValue ? (
              <span className="widget-text">Click on two or more points to get distance</span>
            ) : (
              <div className="measurement-display">Distance: {distanceValue} km</div>
            )}
            {mode === "distance" || distanceValue ? (
              <button className="ccl-button ccl-button--default ccl-button--default-neg" onClick={stopMeasurement} aria-label={distanceValue ? "Clear" : "Stop"}>
                {distanceValue ? "Clear" : "Stop"}
              </button>
            ) : (
              <button className="ccl-button ccl-button--default" onClick={(e) => setActiveMode("distance", e)} aria-label="Start">
                Start
              </button>
            )}
          </div>
          <div className="measurement-item">
            <img src={Area} alt="Area" />
            {!areaValue ? (
              <span className="widget-text">Click on three or more points to get area</span>
            ) : (
              <div className="measurement-display">Area: {areaValue} km²</div>
            )}
            {mode === "area" || areaValue ? (
              <button className="ccl-button ccl-button--default ccl-button--default-neg" onClick={stopMeasurement} aria-label={areaValue ? "Clear" : "Stop"}>
                {areaValue ? "Clear" : "Stop"}
              </button>
            ) : (
              <button className="ccl-button ccl-button--default" onClick={(e) => setActiveMode("area", e)} aria-label="Start">
                Start
              </button>
            )}
          </div>
        </div>
      )}
  </>
  );
};

export default MeasureControl;
